import {
  DEFAULT_RUN_SETTINGS,
  serializeRunSettings,
  type RunSettings,
} from "@/lib/run-settings";
/* eslint-disable no-console */
import { ParsedWorkflow } from "@/services/Spotflow/spotflow";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Edge as ReactFlowEdge,
  Connection,
  ReactFlowInstance,
  XYPosition,
  addEdge,
  MarkerType,
  reconnectEdge,
  useUpdateNodeInternals,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import { SpotToast } from "@/components/SpotToast";
import {
  NodeDefinition,
  GetTriggersResponse,
  WorkflowService,
  DataType,
  FlowTypes,
  TypeDefinition,
  UpdateWorkflowRequest,
  ConfigResponse,
} from "@/generated";
import { UseFormReturn } from "react-hook-form";
import { NodesEnum } from "../CustomNodes/configs";
import { IODefinition, NoxusReactflowNode } from "@/types/spotflow";
import { IDrawerControls } from "../ConfigDrawer/useDrawerControls";
import { findMaxStringValue } from "@/utils/generic";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ITypeDictValue } from "@/components/Form/TypeDictField";
import { UseDisclosureReturn } from "@/utils/useDisclosure";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import apiClient from "@/services/api-client";
import { getConnectorDataType } from "@/components/Spotflow/CustomNodes/BaseNode/CustomHandle";
import { IWorkflowState } from "../Tutorial/useTutorial";
import * as Sentry from "@sentry/nextjs";
import { useSpotUser } from "@/utils/spotUser";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import {
  activeFlowIdAtom,
  addNewNodeAtom,
  areConfigEndpointsLoadedAtom,
  edgesAtom,
  flushEditorSaveSignalAtom,
  hasQueueToSaveAtom,
  hasQueueToUpdateConnectorsAtom,
  isValidDataAtom,
  nodesAtom,
  saveStateAtom,
  runSettingsAtom,
  saveWorkflowHashAtom,
} from "@/store/editor-jotai";
import { FFService } from "@/services/FeatureFlags/FeatureFlags";
import { useAtomCallback } from "jotai/utils";

export interface IExceptionTooltipLabel {
  message: string;
  nodeId: string;
  node_name: string;
  color: string;
  image: string;
}

interface IFormData {
  [key: string]: {
    [key: string]: string;
  };
}

let deleting_node_ids: string[] = [];

export const createFlowDefinition = (
  nodes: NoxusReactflowNode[],
  edges: ReactFlowEdge[],
  formData: IFormData,
  runSettings: RunSettings = DEFAULT_RUN_SETTINGS
) => {
  const parsedEdges = edges.map((e) => {
    const getParsedEdge = ({
      node_id,
      edge_name,
    }: {
      node_id: string;
      edge_name?: string | null;
    }) => {
      if (edge_name?.includes("::")) {
        const [name, key] = edge_name.split("::");
        return {
          connector_name: name,
          node_id,
          key,
        };
      } else {
        return {
          connector_name: edge_name || "",
          node_id,
        };
      }
    };
    const to_id: {
      connector_name: string;
      node_id: string;
      key?: string;
    } = getParsedEdge({ edge_name: e.targetHandle, node_id: e.target });

    const from_id: {
      connector_name: string;
      node_id: string;
      key?: string;
    } = getParsedEdge({ edge_name: e.sourceHandle, node_id: e.source });

    return {
      id: e.id,
      from_id,
      to_id,
    };
  });

  const mainFlowDefinition = {
    ...serializeRunSettings(runSettings),
    nodes: nodes.map((n) => {
      const config = Object.keys(formData).includes(n.id) ? formData[n.id] : {};

      // For BE reasons, we need to remove all empty fields before sending them
      const node_config = Object.entries(config)
        .filter(([_, value]) => value !== "")
        .reduce(
          (acc, [key, value]) => {
            acc[key] = value;
            return acc;
          },
          {} as Record<string, any>
        );

      return {
        id: n.id,
        type: n.data.type as NodesEnum,
        name: n.data.name,
        node_config,
        subflow_config: n.data.config.subflow_config,
        connector_config: {
          inputs: n.data.config?.inputs,
          outputs: n.data.config?.outputs,
        },
        display: {
          position: {
            x: n.position.x,
            y: n.position.y,
          },
          size: {
            width: n.width,
            height: n.height,
          },
        },
        plugin_name: n.data.plugin_name,
        ...(n.data.credential_ids
          ? { credential_ids: n.data.credential_ids }
          : {}),
        ...(n.parentId ? { parent_id: n.parentId } : {}),
      };
    }),
    edges: parsedEdges,
  };

  return { mainFlowDefinition };
};

interface IHistory {
  nodes: NoxusReactflowNode[];
  edges: ReactFlowEdge[];
  config: Record<string, any>;
}

export const useEditorControls = ({
  nodesDefinition,
  workflow,
  reactFlowInstance,
  form,
  drawerControls,
  cantEdit,
  overwriteSaveModalControls,
  isOlderVersion,
}: {
  nodesDefinition?: NodeDefinition[];
  workflow?: ParsedWorkflow;
  reactFlowInstance?: ReactFlowInstance;
  form?: UseFormReturn<any>;
  drawerControls: IDrawerControls;
  triggersData?: GetTriggersResponse;
  cantEdit?: boolean;
  overwriteSaveModalControls: UseDisclosureReturn;
  isOlderVersion?: boolean;
}) => {
  const { spotGroup } = useSpotUser();

  const updateNodeInternals = useUpdateNodeInternals();

  const [nodes, setNodes] = useAtom(nodesAtom);
  const runSettings = useAtomValue(runSettingsAtom);
  const [edges, setEdges] = useAtom(edgesAtom);

  const queryClient = useQueryClient();

  const dragRef = useRef<any>(null);
  const hasPendingValidationRef = useRef(false);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  nodesRef.current = nodes;
  edgesRef.current = edges;

  const [history, setHistory] = useState<{
    redo: IHistory[];
    undo: IHistory[];
  }>({
    redo: [],
    undo: [],
  });
  const debouncedFormChangeTimeout = useRef<any>(null);
  const debouncedSaveTimeout = useRef<any>(null);
  const getNodeById = useAtomCallback(
    useCallback((get, _set, nodeId: string) => {
      const currentNodes = get(nodesAtom);
      return currentNodes.find((nd) => nd.id === nodeId);
    }, [])
  );

  const [saveState, setSaveState] = useAtom(saveStateAtom);

  const updateConnectorsSuccess = useCallback(
    (data?: FlowTypes) => {
      if (!data) {
        setSaveState({
          status: "saved",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const nodes_to_update: Record<string, NoxusReactflowNode> = {};

      for (const [key, def] of Object.entries(data.input_types)) {
        const exploded = key.split("::");
        const is_dynamic_connector = exploded.length > 2;
        const nodeId = exploded[0];
        const connectorName = exploded[1];
        const connectorKey = is_dynamic_connector ? exploded[2] : null;
        const node = getNodeById(nodeId) as NoxusReactflowNode | undefined;

        if (!node) break;
        const copy = structuredClone(node);

        const inputs = Object.keys(nodes_to_update).includes(nodeId)
          ? (nodes_to_update[nodeId]?.data.config.inputs as IODefinition[])
          : (copy.data.config.inputs as IODefinition[]);

        copy.data.config.inputs = inputs.map((input, index) => {
          if (input.name === connectorName) {
            // For variable_type_size_connector with a specific key,
            // only update type_definitions — not the top-level definition
            // (which is the default type for keys without explicit overrides)
            if (connectorKey && input.type === "variable_type_size_connector") {
              return {
                ...input,
                type_definitions: {
                  ...(input.type_definitions || {}),
                  [connectorKey]: {
                    data_type: def.data_type,
                    is_list: def.is_list,
                  },
                },
              };
            }

            const updatedInput = {
              ...input,
              definition: {
                ...input.definition,
                data_type: def.data_type,
                is_list: def.is_list,
              },
            };

            if (
              connectorKey &&
              input.type_definitions &&
              input.type_definitions[connectorKey]
            ) {
              updatedInput.type_definitions = {
                ...input.type_definitions,
                [connectorKey]: {
                  ...input.type_definitions[connectorKey],
                  data_type: def.data_type,
                  is_list: def.is_list,
                },
              };
            }

            return updatedInput;
          } else {
            return input;
          }
        });

        nodes_to_update[nodeId] = copy;
      }

      for (const [key, def] of Object.entries(data.output_types)) {
        const exploded = key.split("::");
        const is_dynamic_connector = exploded.length > 2;
        const nodeId = exploded[0];
        const connectorName = exploded[1];
        const connectorKey = is_dynamic_connector ? exploded[2] : null;
        const node = nodes_to_update[nodeId] || getNodeById(nodeId);

        if (!node) break;
        const copy = structuredClone(node);

        const outputs = Object.keys(nodes_to_update).includes(nodeId)
          ? (nodes_to_update[nodeId]?.data.config.outputs as IODefinition[])
          : (copy.data.config.outputs as IODefinition[]);

        copy.data.config.outputs = outputs.map((output) => {
          if (output.name === connectorName) {
            if (
              connectorKey &&
              output.type === "variable_type_size_connector"
            ) {
              return {
                ...output,
                type_definitions: {
                  ...(output.type_definitions || {}),
                  [connectorKey]: {
                    data_type: def.data_type,
                    is_list: def.is_list,
                  },
                },
              };
            }

            const updatedOutput = {
              ...output,
              definition: {
                ...output.definition,
                data_type: def.data_type,
                is_list: def.is_list,
              },
            };

            if (
              connectorKey &&
              output.type_definitions &&
              output.type_definitions[connectorKey]
            ) {
              updatedOutput.type_definitions = {
                ...output.type_definitions,
                [connectorKey]: {
                  ...output.type_definitions[connectorKey],
                  data_type: def.data_type,
                  is_list: def.is_list,
                },
              };
            }

            return updatedOutput;
          } else {
            return output;
          }
        });
        nodes_to_update[nodeId] = copy;
      }

      deleting_node_ids = [];

      setNodes((nds) =>
        nds
          .filter((n) => !deleting_node_ids.includes(n.id))
          .map((n) => {
            if (Object.keys(nodes_to_update).includes(n.id)) {
              const updatedNode = nodes_to_update[n.id];
              // Preserve current position and dimensions to avoid reverting during drag if a save is ongoing
              return {
                ...updatedNode,
                position: n.position, // Keep current position
                width: n.width, // Keep current width
                height: n.height, // Keep current height
                selected: n.selected, // Keep current selected state
              };
            }
            return n;
          })
      );
      updateNodeInternals(Object.keys(nodes_to_update));
      setSaveState({
        status: "saved",
        timestamp: new Date().toISOString(),
      });
    },
    [getNodeById, setNodes, updateNodeInternals, setSaveState]
  );

  const getCurrentFlowDefinition = useAtomCallback(
    useCallback(
      (get) => {
        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);
        return createFlowDefinition(
          currentNodes,
          currentEdges,
          form?.getValues(),
          runSettings
        ).mainFlowDefinition;
      },
      [form, runSettings]
    )
  );

  const [saveWorkflowHash, setSaveWorkflowHash] = useAtom(saveWorkflowHashAtom);
  const initialWorkflowHash = useRef(workflow?.workflow_hash);

  const updateConnectorsFromBackendMutationHandler = ({
    id,
    req,
  }: {
    id: string;
    req: UpdateWorkflowRequest;
    disableUpdateConnector?: boolean;
  }) => {
    return WorkflowService.saveWorkflow({
      path: { group_id: spotGroup.id, workflow_id: id },
      body: {
        ...req,
        workflow_hash: saveWorkflowHash || initialWorkflowHash.current,
      },
    }).then((res) => {
      return res.data;
    });
  };

  const updateConnectorsFromBackendMutation = useMutation({
    mutationFn: updateConnectorsFromBackendMutationHandler,

    onSuccess: (data, { disableUpdateConnector }) => {
      if (!disableUpdateConnector) updateConnectorsSuccess(data);
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS_ID],
      });
      setSaveWorkflowHash(data.workflow_hash);
    },

    onError: (error, { id }) => {
      if ((error as any)?.response.status === 409) {
        overwriteSaveModalControls.onOpen();
        return;
      }

      if (id) {
        Sentry.captureException(error);
        console.error(error);
        setSaveState({
          status: "errored",
          timestamp: new Date().toISOString(),
        });
      }
    },
  });

  const fastSaveMutationHandler = ({
    id,
    req,
    force,
  }: {
    id: string;
    req: UpdateWorkflowRequest;
    disableUpdateConnector?: boolean;
    force?: boolean;
  }) => {
    return WorkflowService.updateWorkflow({
      path: {
        group_id: spotGroup.id,
        workflow_id: id,
      },
      body: {
        ...req,
        workflow_hash: saveWorkflowHash || initialWorkflowHash.current,
      },
      query: { force },
    });
  };

  const fastSaveMutation = useMutation({
    mutationFn: fastSaveMutationHandler,

    onSuccess: (data, { disableUpdateConnector }) => {
      if (!disableUpdateConnector) updateConnectorsSuccess();
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS_ID],
      });
      setSaveWorkflowHash(data.data.workflow_hash || undefined);
    },

    onError: (error) => {
      console.error(error);
      if ((error as any)?.response.status === 409) {
        overwriteSaveModalControls.onOpen();
      } else {
        SpotToast.error(
          "There was an error automatically saving. If this problem persists, please contact support."
        );
        setSaveState({
          status: "errored",
          timestamp: new Date().toISOString(),
        });
        Sentry.captureException(error);
      }
    },
  });

  const asyncFastSave = useAtomCallback(
    useCallback(
      async (get, _set, isForceSave?: boolean) => {
        if (isOlderVersion) return;
        if (cantEdit) return;

        clearTimeout(debouncedFormChangeTimeout.current);
        clearTimeout(debouncedSaveTimeout.current);
        debouncedSaveTimeout.current = null;
        debouncedFormChangeTimeout.current = null;

        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);

        const newWorkflowReq = {
          definition: createFlowDefinition(
            currentNodes,
            currentEdges,
            form?.getValues(),
            runSettings
          ).mainFlowDefinition,
        };

        try {
          const result = await fastSaveMutation.mutateAsync({
            id: workflow!.id,
            req: newWorkflowReq,
            force: isForceSave,
          });
          return result;
        } catch (e) {
          if ((e as any)?.response.status === 409) {
            overwriteSaveModalControls.onOpen();
            return;
          }
          console.error(e);
          SpotToast.error(
            "There was an error automatically saving. If this problem persists, please contact support."
          );
          Sentry.captureException(e);
        }
      },
      [
        runSettings,
        cantEdit,
        form,
        fastSaveMutation,
        workflow,
        overwriteSaveModalControls,
        isOlderVersion,
      ]
    )
  );

  // This is used to save the workflow when the user changes the tab
  // We only need to save when the user changes the tab if there are unsaved changes
  // The way we know if there are unsaved changes is by checking if the debouncedSaveTimeout is not null
  const asyncFastSaveForTabChange = useCallback(async () => {
    if (debouncedSaveTimeout.current) {
      clearTimeout(debouncedSaveTimeout.current);
      debouncedSaveTimeout.current = null;
      return await asyncFastSave();
    }
  }, [asyncFastSave]);

  const [isValidData, setIsValidData] = useAtom(isValidDataAtom);

  const isValidMutation = useMutation({
    mutationFn: (definition: { [key: string]: unknown }) => {
      // Read-only viewers can't change the definition, so the validity
      // panel always reflects the saved state — skip the autosave-driven
      // re-validation. The endpoint is run-perm gated and would 403.
      // ``exceptions`` defaults to an empty record so BaseNode's
      // ``isValidData?.exceptions[nodeId]`` lookup doesn't blow up.
      if (cantEdit) {
        return Promise.resolve({
          data: { is_valid: true, exceptions: {} },
        } as Awaited<
          ReturnType<typeof WorkflowService.getIsValidWorkflowFromDefinition>
        >);
      }
      return WorkflowService.getIsValidWorkflowFromDefinition({
        body: {
          definition:
            definition ||
            createFlowDefinition(nodes, edges, form?.getValues())
              .mainFlowDefinition,
        },
        path: {
          group_id: spotGroup.id,
        },
      });
    },

    onSuccess: (data) => {
      setIsValidData(data.data);
      hasPendingValidationRef.current = false;
    },
  });

  const disableAutosave = FFService.getVar("EDITOR_DISABLE_AUTOSAVE");

  const saveAndUpdateTypes = useAtomCallback(
    useCallback(
      (
        get,
        set,
        args: {
          state?: {
            nds?: NoxusReactflowNode[];
            eds?: ReactFlowEdge[];
            title?: string;
          };
          hist?: {
            nds?: NoxusReactflowNode[];
            eds?: ReactFlowEdge[];
            title?: string;
          };
          isFastSave?: boolean;
          disableUpdateConnector?: boolean;
          isManualSave?: boolean;
        }
      ) => {
        const {
          state,
          hist,
          isFastSave,
          disableUpdateConnector,
          isManualSave,
        } = args;

        const nds = state?.nds || get(nodesAtom);
        const eds = state?.eds || get(edgesAtom);
        const activeWorkflowId = get(activeFlowIdAtom);

        if (activeWorkflowId !== workflow?.id) {
          SpotToast.warn(
            "The save wasn't successful. It this problem persists, please contact support."
          );
          return;
        }

        clearTimeout(debouncedFormChangeTimeout.current);
        clearTimeout(debouncedSaveTimeout.current);
        debouncedSaveTimeout.current = null;
        debouncedFormChangeTimeout.current = null;
        if (isOlderVersion) return;
        if (disableAutosave && !isManualSave) return;
        const newWorkflowReq = {
          definition: createFlowDefinition(
            nds,
            eds,
            form?.getValues(),
            runSettings
          ).mainFlowDefinition,
        };
        if (cantEdit) return;
        const history_entry = {
          nodes: hist?.nds || nds,
          edges: hist?.eds || eds,
          config: form?.getValues() as Record<string, any>,
        };
        setHistory({ redo: [], undo: [...history.undo, history_entry] });
        if (isFastSave && !hasPendingValidationRef.current) {
          fastSaveMutation.mutate({
            id: workflow!.id,
            req: newWorkflowReq,
            disableUpdateConnector,
          });
        } else {
          isValidMutation.mutate(newWorkflowReq.definition);
          updateConnectorsFromBackendMutation.mutate({
            id: workflow?.id || "",
            req: newWorkflowReq,
            disableUpdateConnector,
          });
        }
      },
      [
        runSettings,
        isOlderVersion,
        disableAutosave,
        form,
        isValidMutation,
        cantEdit,
        history.undo,
        fastSaveMutation,
        workflow,
        updateConnectorsFromBackendMutation,
      ]
    )
  );

  const debouncedSave = useCallback(
    (
      state?: {
        nds?: NoxusReactflowNode[];
        eds?: ReactFlowEdge[];
        title?: string;
      },
      hist?: { nds?: NoxusReactflowNode[]; eds?: ReactFlowEdge[] },
      isFastSave?: boolean,
      disableUpdateConnector?: boolean,
      isManualSave?: boolean
    ) => {
      if (!isFastSave) {
        hasPendingValidationRef.current = true;
      }

      clearTimeout(debouncedSaveTimeout.current);
      debouncedSaveTimeout.current = setTimeout(() => {
        debouncedSaveTimeout.current = null;

        saveAndUpdateTypes({
          state,
          hist,
          isFastSave,
          disableUpdateConnector,
          isManualSave,
        });
      }, 1000);
    },
    [saveAndUpdateTypes]
  );

  useEffect(() => {
    return () => {
      clearTimeout(debouncedSaveTimeout.current);
      debouncedSaveTimeout.current = null;
      clearTimeout(debouncedFormChangeTimeout.current);
      debouncedFormChangeTimeout.current = null;
      hasPendingValidationRef.current = false;
    };
  }, []);

  const isValidConnection = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        connection: any,
        forced_state?: { nodes?: NoxusReactflowNode[] }
      ) => {
        if (isOlderVersion) return true;

        let sourceNodeOutputs: IODefinition[] = [];
        let targetNodeInputs: IODefinition[] = [];
        let targetNodeType;
        let targetNode: NoxusReactflowNode | undefined;
        let sourceNodeInputs: IODefinition[] = [];
        let sourceNodeType;

        const nodesToSearch = forced_state?.nodes || get(nodesAtom);
        const currentEdges = get(edgesAtom);

        for (const nd of nodesToSearch) {
          if (nd.id === connection.source) {
            sourceNodeOutputs = nd.data.config.outputs || [];
            sourceNodeInputs = nd.data.config.inputs || [];
            sourceNodeType = nd.data.type;
          } else if (nd.id === connection.target) {
            targetNodeInputs = nd.data.config.inputs || [];
            targetNodeType = nd.data.type;
            targetNode = nd;
          }
        }

        let source_connector_key: string | null = null;

        const sourceHandle = sourceNodeOutputs.find((handle) => {
          if (handle.visible === false) {
            return false;
          }
          if (connection.sourceHandle.includes("::")) {
            const [handleName, handleKey] = (
              connection.sourceHandle as string
            ).split("::");
            source_connector_key = handleKey;

            return (
              handle.name === handleName && handle.keys?.includes(handleKey)
            );
          } else {
            return handle.name === connection.sourceHandle;
          }
        }) as IODefinition;

        let target_connector_key: string | null = null;

        const targetHandle = targetNodeInputs.find((handle) => {
          if (handle.visible === false) {
            return false;
          }
          if (connection.targetHandle.includes("::")) {
            const [handleName, handleKey] = (
              connection.targetHandle as string
            ).split("::");
            target_connector_key = handleKey;
            return (
              handle.name === handleName && handle.keys?.includes(handleKey)
            );
          } else {
            return handle.name === connection.targetHandle;
          }
        }) as IODefinition;

        if (
          !sourceHandle ||
          !targetHandle ||
          sourceHandle.type === "variable_type_input" ||
          sourceHandle.type === "variable_type_output"
        ) {
          return false;
        }

        const sourceTypeSetByRule = sourceHandle.rules?.find(
          (rule) => rule.type === "type_set_by"
        );
        const targetTypeSetByRule = targetHandle.rules?.find(
          (rule) => rule.type === "type_set_by"
        );

        // If the form is not loaded and some connections depend on it for the type
        // we return true to deleting edges that could be correct.
        if (!form && (sourceTypeSetByRule || targetTypeSetByRule)) {
          console.log(
            `[debug] bailing "${sourceTypeSetByRule?.connector}" or "${targetTypeSetByRule?.connector}" need form and it isn't defined!!`
          );
          return true;
        }

        const sourceDataType = sourceTypeSetByRule
          ? form?.getValues(
              `${connection.source}.${sourceTypeSetByRule.config_source}`
            ) || "str"
          : sourceHandle.type === "variable_type_size_connector"
            ? getConnectorDataType(sourceHandle, source_connector_key || "")
            : sourceHandle.definition.data_type;

        const targetDataType = targetTypeSetByRule
          ? form?.getValues(
              `${connection.target}.${targetHandle.rules?.[0].config_source}`
            ) || "str"
          : targetHandle.type === "variable_type_size_connector"
            ? getConnectorDataType(targetHandle, target_connector_key || "")
            : targetHandle.definition.data_type;

        //We don't want to check for connections to/from this node, hence the filter
        const targetHandleHasConnections = currentEdges
          .filter(
            (e) =>
              !(
                e.target === connection.target &&
                e.source === connection.source &&
                e.sourceHandle === connection.sourceHandle &&
                e.targetHandle === connection.targetHandle
              )
          )
          .some(
            (e) =>
              e.target === connection.target &&
              e.targetHandle === connection.targetHandle
          );

        const forbiddenListInputNodes = ["SplitToListNode"];
        const forbiddenNonListInputNodes = ["MergeListNode"];

        // We want to automatically set the type of the target node to the source node type if the target node is an OutputNode
        if (targetNode?.data.type === "OutputNode") {
          if (
            targetDataType !== sourceDataType &&
            !targetHandleHasConnections
          ) {
            form?.setValue(`${targetNode.id}.type`, sourceDataType);
            return true;
          }
        }

        const sourceDefinition =
          (sourceHandle.type === "variable_type_size_connector"
            ? sourceHandle.type_definitions?.[source_connector_key || ""]
            : sourceHandle.definition) || sourceHandle.definition;

        /* Validity function */
        const isValid =
          sourceDataType &&
          targetDataType &&
          !targetHandleHasConnections &&
          sourceDataType === targetDataType &&
          connection.source !== connection.target &&
          !(
            forbiddenListInputNodes.includes(targetNodeType) &&
            sourceDefinition?.is_list
          ) &&
          !(
            forbiddenNonListInputNodes.includes(targetNodeType) &&
            !sourceDefinition?.is_list
          );

        /* This if handles the edge coloring */

        if (!isValid) {
          setConnectionLineStyle({
            stroke: "#ea3e66",
            strokeWidth: 2,
          });

          setTimeout(
            () =>
              setConnectionLineStyle({
                transition: "stroke 0.5s, stroke-width 0.5s",
              }),
            500
          );
        }

        return isValid;
      },
      [form, isOlderVersion]
    )
  );

  const updateMultipleNodes = useCallback(
    (
      updated_nodes: NoxusReactflowNode[],
      options?: {
        updatesHandles?: boolean;
        clearDanglingEdges?: boolean;
        ignoreOutputBypassOnClearingDanglingEdges?: boolean;
        updatesDataOnly?: boolean;
      }
    ) => {
      const new_nodes: NoxusReactflowNode[] = [];
      setNodes((nds) =>
        nds.map((n) => {
          const node_being_updated = updated_nodes.find(
            ({ id }) => id === n.id
          );
          let updated_node = node_being_updated || n;
          if (options?.updatesDataOnly) {
            updated_node = {
              ...n,
              data: updated_node.data,
            };
          }

          const final_node = {
            ...updated_node,
            selected: n.selected,
          };
          new_nodes.push(final_node);
          return final_node;
        })
      );

      if (options?.clearDanglingEdges) {
        const nodes_type_mapping = new_nodes.reduce(
          (acc, node) => {
            acc[node.id] = node.data.type || "";
            return acc;
          },
          {} as Record<string, string>
        );

        setEdges((eds) => {
          const current_edge_count = eds.length;
          const new_edges = eds.filter((e) => {
            // If the target node is an OutputNode, we are using an "optimistic", assuming the connection to that node is always valid.
            // This is because, on isValidConnection, we are checking if the target node is an OutputNode and if the types match.
            // If the types don't match, we set the type of the target node to the source node type and return true,
            // which means the edge will be preserved.
            if (
              nodes_type_mapping[e.target] === "OutputNode" &&
              !options?.ignoreOutputBypassOnClearingDanglingEdges
            ) {
              return true;
            }

            return isValidConnection(e, { nodes: new_nodes });
          });

          if (new_edges.length === 0 && current_edge_count > 5) {
            //We had a bug where, for some reasons, all edges were being deleted.
            //To avoid having this bug which is very serious when users have a very big workflow
            //we check if they have more than 5 edges (5 was an arbitrary number). If they do and
            // they suddently drop to 0, we restore the edges
            return eds;
          }
          return new_edges;
        });
      }

      if (options?.updatesHandles) {
        updated_nodes.map(({ id }) => updateNodeInternals(id));
      }

      return new_nodes;
    },
    [isValidConnection, setEdges, setNodes, updateNodeInternals]
  );

  const [nodes_fetching_config, set_nodes_fetching_config] = useState<string[]>(
    []
  );

  const [hasQueueToUpdateConnectors, setHasQueueToUpdateConnectors] = useAtom(
    hasQueueToUpdateConnectorsAtom
  );

  useEffect(() => {
    if (hasQueueToUpdateConnectors) {
      getDynamicConfigsFromBackend(hasQueueToUpdateConnectors);
      setHasQueueToUpdateConnectors(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, hasQueueToUpdateConnectors]);

  // This is used to check if the nodes have been updated before the debounce,
  // so we only call the config endpoints required
  const nodesUpdatedBeforeDebounceRef = useRef<string[]>([]);

  const setAreConfigEndpointsLoaded = useSetAtom(areConfigEndpointsLoadedAtom);

  const drawerControlsRef = useRef(drawerControls);
  drawerControlsRef.current = drawerControls;

  const getDynamicConfigsFromBackend = useAtomCallback(
    useCallback(
      async (get, _set, nodeId?: string) => {
        if (isOlderVersion) return;
        // Read-only viewers don't need dynamic config refresh — those
        // endpoints are integrations:edit gated and only drive the
        // editor's autosave loop. Mark configs as "loaded" so loading
        // states resolve and the canvas renders with the static
        // definition-time config.
        if (cantEdit) {
          setAreConfigEndpointsLoaded(true);
          return;
        }
        clearTimeout(debouncedSaveTimeout.current);
        debouncedSaveTimeout.current = null;

        const currentNodes = get(nodesAtom);
        const updated_nodes: NoxusReactflowNode[] = [];

        const configNodes = (currentNodes as NoxusReactflowNode[]).filter(
          (n) => {
            if (nodeId) {
              return n.id === nodeId && n.data.config.config_endpoint;
            } else {
              return n.data.config.config_endpoint;
            }
          }
        );

        const updating_ids = configNodes.map((n) => n.id);
        set_nodes_fetching_config(updating_ids);

        if (configNodes.length === 0) {
          return;
        }

        clearTimeout(debouncedFormChangeTimeout.current);

        const getFormValues = (n: NoxusReactflowNode) => {
          const defaultConfig = Object.entries(
            n.data.config.config as { [key: string]: { default: any } }
          ).reduce(
            (acc, [key, value]) => {
              acc[key] = value.default;
              return acc;
            },
            {} as { [key: string]: any }
          );

          return {
            ...(form?.getValues(n.id) || defaultConfig),
            _inputs: n.data.config.inputs,
            _outputs: n.data.config.outputs,
          };
        };

        const extractNodeType = (endpoint: string): string => {
          // config_endpoint format: "/nodes/{NodeType}/config"
          const parts = endpoint.split("/").filter(Boolean);
          const nodesIdx = parts.indexOf("nodes");
          return nodesIdx >= 0 && nodesIdx + 1 < parts.length
            ? parts[nodesIdx + 1]
            : endpoint;
        };

        const applyConfigResponse = (
          node: NoxusReactflowNode,
          data: ConfigResponse
        ) => {
          const {
            inputs,
            outputs,
            config,
            ready,
            title,
            image,
            config_values_overrides,
            validation,
            credential_errors,
          } = data;
          const referenced_assets = (data as { referenced_assets?: unknown[] })
            .referenced_assets;

          const copyNode = structuredClone(node);

          // Preserve credential picker fields from the static definition.
          // They are defined in the node definition (not in the config endpoint
          // response) so we merge them back in at the top.
          const staticConfig = node.data.config.config as
            | Record<string, any>
            | undefined;
          const credentialFields: Record<string, any> = {};
          if (staticConfig) {
            for (const [key, value] of Object.entries(staticConfig)) {
              if (key.startsWith("_credential_id_")) {
                credentialFields[key] = value;
              }
            }
          }

          copyNode.data.config.inputs = inputs;
          copyNode.data.config.outputs = outputs;
          copyNode.data.config.config = { ...credentialFields, ...config };
          copyNode.data.config.is_valid = ready;
          copyNode.data.config.validation = validation;
          copyNode.data.config.credential_errors = credential_errors ?? {};
          copyNode.data.config.referenced_assets = referenced_assets ?? [];

          if (title) {
            copyNode.data.config.title = title;
            copyNode.data.name = title;
            // BaseNode renders ``form.getValues(`${nodeId}.label`) || name``
            // (CustomNodes/BaseNode/BaseNode.tsx). ``addNewNodeAtom`` seeds
            // every config field's default into the form — including the
            // ``label`` field whose default is the static node title — so
            // without this override the form's stale ``.label`` keeps
            // shadowing ``data.name`` after a config-driven title change.
            if (form?.getValues(`${copyNode.id}.label`) !== title) {
              form?.setValue(`${copyNode.id}.label`, title);
            }
          }
          if (image) {
            copyNode.data.config.image = image;
            // Persist the config-driven image into the form so it survives
            // workflow save → reload → run-progress render. Both the canvas
            // (editor.ts) and run view read ``node_config._dynamic_image``
            // as a fallback over the unbranded static ``nodeDef.image``.
            if (form?.getValues(`${copyNode.id}._dynamic_image`) !== image) {
              form?.setValue(`${copyNode.id}._dynamic_image`, image);
            }
          }
          updated_nodes.push(copyNode);

          const currentDrawerControls = drawerControlsRef.current;
          if (currentDrawerControls.openNodeDef?.id === copyNode.id) {
            currentDrawerControls.setOpenNodeDef({
              id: copyNode.id,
              node: copyNode.data.config,
            });
          }

          if (config_values_overrides) {
            Object.entries(config_values_overrides).forEach(([key, value]) => {
              if (form?.getValues(`${copyNode.id}.${key}`) !== value) {
                form?.setValue(`${copyNode.id}.${key}`, value);
              }
            });
          }
        };

        try {
          if (nodeId) {
            // Single node refresh (user changed a config) — use per-node endpoint with skip_cache
            const node = configNodes[0];
            const formValues = getFormValues(node);
            const endpoint = node.data.config.config_endpoint;
            const wfIdParam = workflow?.id ? `&workflow_id=${workflow.id}` : "";
            const res = await apiClient.post(
              `/api/backend/groups/${spotGroup.id}${endpoint.startsWith("/") ? "" : "/"}${endpoint}?skip_cache=true${wfIdParam}`,
              formValues || {}
            );
            applyConfigResponse(node, res.data as ConfigResponse);
          } else {
            // Initial load — batch all config requests into one call
            const items = configNodes.map((n) => ({
              instance_id: n.id,
              node_type: extractNodeType(n.data.config.config_endpoint),
              config: getFormValues(n),
            }));

            const res = await apiClient.post(
              `/api/backend/groups/${spotGroup.id}/nodes/batch-config`,
              { items, workflow_id: workflow?.id ?? null }
            );

            const results = res.data.results as Record<
              string,
              ConfigResponse | null
            >;

            for (const node of configNodes) {
              const configData = results[node.id];
              if (configData) {
                applyConfigResponse(node, configData);
              }
            }
          }
        } catch (e) {
          set_nodes_fetching_config([]);
          setAreConfigEndpointsLoaded(true);
          Sentry.captureException(e);
          return;
        }

        const nds = updateMultipleNodes(updated_nodes, {
          updatesHandles: true,
          clearDanglingEdges: true,
          ignoreOutputBypassOnClearingDanglingEdges: true,
          updatesDataOnly: true,
        });

        set_nodes_fetching_config([]);
        nodesUpdatedBeforeDebounceRef.current = [];
        setAreConfigEndpointsLoaded(true);
        return nds;
      },
      [
        isOlderVersion,
        cantEdit,
        updateMultipleNodes,
        setAreConfigEndpointsLoaded,
        form,
        spotGroup.id,
        workflow?.id,
      ]
    )
  );

  const [hasQueueToSave, setHasQueueToSave] = useAtom(hasQueueToSaveAtom);

  useEffect(() => {
    if (hasQueueToSave) {
      setHasQueueToSave(false);
      debouncedSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasQueueToSave]);

  const flushSaveSignal = useAtomValue(flushEditorSaveSignalAtom);
  useEffect(() => {
    if (flushSaveSignal === 0) return;
    if (debouncedSaveTimeout.current) {
      saveAndUpdateTypes({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushSaveSignal]);

  const handleFormChanged = useAtomCallback(
    useCallback(
      async (get, _set, data: Record<string, any>, nodeId?: string) => {
        const currentNodes = get(nodesAtom);

        const node = currentNodes.find((n) => n.id === nodeId);
        if (node?.type === "note") {
          setHasQueueToSave(true);
          return;
        }

        const updated_nodes: NoxusReactflowNode[] = currentNodes.map((n) => {
          const new_name = form?.getValues(`${n.id}.label`) || n.data.name;
          const onErrorOutput = data[`${n.id}`]?.["has_on_error_output"];
          const onErrorBehavior = data[`${n.id}`]?.["on_error_behavior"];
          const showOnError = onErrorOutput && onErrorBehavior !== "fail";
          const outputs = n.data.config.outputs as IODefinition[];

          const updatedOutputs = outputs?.map((output) =>
            output.name === "on_error"
              ? { ...output, visible: showOnError ?? false }
              : output
          );

          return {
            ...n,
            data: {
              ...n.data,
              name:
                n.data.type === "InputNode" || n.data.type === "OutputNode"
                  ? new_name
                  : n.data.name,
              config: {
                ...n.data.config,
                outputs: updatedOutputs,
              },
            },
          };
        });

        updateMultipleNodes(updated_nodes, {
          updatesHandles: true,
          ignoreOutputBypassOnClearingDanglingEdges: true,
          clearDanglingEdges: true,
        });

        await getDynamicConfigsFromBackend(nodeId);

        setHasQueueToSave(true);
      },
      [
        form,
        getDynamicConfigsFromBackend,
        setHasQueueToSave,
        updateMultipleNodes,
      ]
    )
  );

  const debouncedIsValidTimeout = useRef<NodeJS.Timeout | null>(null);

  const debouncedHandleFormChanged = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        data: Record<string, any>,
        nodeId?: string,
        changedValueWasBoolean?: boolean
      ) => {
        if (isOlderVersion) return;

        if (changedValueWasBoolean) {
          handleFormChanged(data, nodeId);
          clearTimeout(debouncedFormChangeTimeout.current);
          debouncedFormChangeTimeout.current = null;
        } else {
          clearTimeout(debouncedFormChangeTimeout.current);
          debouncedFormChangeTimeout.current = setTimeout(() => {
            debouncedFormChangeTimeout.current = null;
            handleFormChanged(data, nodeId);
          }, 750);
        }

        clearTimeout(debouncedIsValidTimeout.current || undefined);
        debouncedIsValidTimeout.current = setTimeout(() => {
          debouncedIsValidTimeout.current = null;

          const currentNodes = get(nodesAtom);
          const currentEdges = get(edgesAtom);

          isValidMutation.mutate(
            createFlowDefinition(currentNodes, currentEdges, form?.getValues())
              .mainFlowDefinition
          );
        }, 750);
      },
      [form, handleFormChanged, isOlderVersion, isValidMutation]
    )
  );

  const [connectionLineStyle, setConnectionLineStyle] = useState({});

  const updateNode = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        id: string,
        node: NoxusReactflowNode,
        options?: { updatesHandles?: boolean; clearDanglingEdges?: boolean }
      ) => {
        if (id !== node.id) return;

        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);

        const new_nodes = currentNodes.map((n) => {
          if (n.id === id) {
            return node;
          }
          return n;
        });

        setNodes(new_nodes);

        if (options?.clearDanglingEdges) {
          const initial_edges_count = currentEdges.length;
          const new_edges = currentEdges.filter((e) =>
            isValidConnection(e, { nodes: new_nodes })
          );
          const should_ignore_edge_deletion =
            new_edges.length === 0 && initial_edges_count > 5;
          if (should_ignore_edge_deletion) {
            // Alert Sentry about the potential edge deletion issue
            Sentry.captureMessage("Potential edge deletion issue detected", {
              level: "warning",
              extra: {
                nodes: currentNodes,
                edges: currentEdges,
              },
            });
          } else {
            //We had a bug where, for some reasons, all edges were being deleted.
            //To avoid having this bug which is very serious when users have a very big workflow
            //we check if they have more than 5 edges (5 was an arbitrary number). If they do and
            // they suddently drop to 0, we restore the edges
            setEdges(new_edges);
          }
        }
        if (options?.updatesHandles) updateNodeInternals(id);
      },
      [isValidConnection, setEdges, setNodes, updateNodeInternals]
    )
  );

  const updateNodeName = useAtomCallback(
    useCallback(
      (get, _set, node_id: string, name: string) => {
        const currentNodes = get(nodesAtom);
        const node = currentNodes.find((n) => n.id === node_id);
        if (!node) return;
        updateNode(
          node_id,
          {
            ...node,
            data: {
              ...node.data,
              name,
            },
          },
          { updatesHandles: true }
        );
        debouncedHandleFormChanged(form?.getValues());
      },
      [debouncedHandleFormChanged, form, updateNode]
    )
  );

  useEffect(() => {
    //We want to get which connectors are lists from the backend when loading the workflow
    const getInitialState = async () => {
      await getDynamicConfigsFromBackend();
      if ((nodes && nodes.length) || (edges && edges.length)) {
        isValidMutation.mutate(
          createFlowDefinition(nodes, edges, form?.getValues())
            .mainFlowDefinition
        );
      }
    };
    getInitialState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = form?.watch((values, { name, type }) => {
      if (!name) return;
      if (
        !type &&
        !name.includes("model") &&
        !name.includes("knowledge_base_name")
      )
        return;

      queryClient.removeQueries({
        queryKey: [DATA_KEYS.WORKFLOW_DEPENDENCIES],
      });

      const nodeId = name.split(".")[0];
      const fieldName = name.split(".")[1];
      if (nodeId) {
        nodesUpdatedBeforeDebounceRef.current.push(nodeId);
      }

      const changedValue = values[nodeId]?.[fieldName];
      const shouldRefreshImmediately =
        typeof changedValue === "boolean" || Array.isArray(changedValue);

      debouncedHandleFormChanged(values, nodeId, shouldRefreshImmediately);

      // if (disableAutosave) {
      setSaveState({
        timestamp: new Date().toISOString(),
        status: "unsaved_changes",
      });
      // }
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, [
    handleFormChanged,
    form,
    debouncedHandleFormChanged,
    queryClient,
    setSaveState,
  ]);

  const handleUndo = useAtomCallback(
    useCallback(
      (get) => {
        if (history.undo.length === 0) return;
        const last = history.undo[history.undo.length - 1];
        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);
        setHistory((h) => {
          return {
            redo: [
              ...h.redo,
              {
                nodes: currentNodes,
                edges: currentEdges,
                config: form?.getValues() as Record<string, any>,
              },
            ],
            undo: h.undo.slice(0, -1),
          };
        });

        setNodes(last.nodes);
        const new_edges = last.edges.filter((e) => {
          const valid = isValidConnection(
            {
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
            },
            { nodes: last.nodes }
          );

          return valid;
        });
        setEdges(new_edges);
        form?.reset(last.config);
        updateNodeInternals(last.nodes.map((n) => n.id));
        debouncedSave({ nds: last.nodes, eds: new_edges });
      },
      [
        debouncedSave,
        form,
        history.undo,
        isValidConnection,
        setEdges,
        setNodes,
        updateNodeInternals,
      ]
    )
  );

  const handleRedo = useAtomCallback(
    useCallback(
      (get) => {
        if (history.redo.length === 0) return;
        const last = history.redo[history.redo.length - 1];
        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);
        setHistory((h) => {
          return {
            undo: [
              ...h.undo,
              {
                nodes: currentNodes,
                edges: currentEdges,
                config: form?.getValues() as Record<string, any>,
              },
            ],
            redo: h.redo.slice(0, -1),
          };
        });
        setNodes(last.nodes);
        const new_edges = last.edges.filter((e) =>
          isValidConnection({
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
          })
        );
        setEdges(new_edges);
        form?.reset(last.config);
        updateNodeInternals(last.nodes.map((n) => n.id));
        debouncedSave({ nds: last.nodes, eds: new_edges });
      },
      [
        debouncedSave,
        form,
        history.redo,
        isValidConnection,
        setEdges,
        setNodes,
        updateNodeInternals,
      ]
    )
  );

  const history_controls = {
    undo: handleUndo,
    redo: handleRedo,
    canUndo: history.undo.length > 0,
    canRedo: history.redo.length > 0,
  };

  const deleteNodesById = useAtomCallback(
    useCallback(
      (get, _set, idsToDelete: string[]) => {
        const currentNodes = get(nodesAtom);
        const nds = currentNodes.filter(
          (node) => !idsToDelete.includes(node.id)
        );
        setNodes(nds);
        debouncedSave({ nds });
      },
      [debouncedSave, setNodes]
    )
  );

  const getKBNode = useAtomCallback(
    useCallback((get, _set, idKB: string) => {
      const currentNodes = get(nodesAtom);
      return currentNodes.find((n) => n.id === idKB);
    }, [])
  );

  const isNodeDefined = useAtomCallback(
    useCallback((get, _set, id: string) => {
      const currentNodes = get(nodesAtom);
      return currentNodes.some((n) => n.id === id);
    }, [])
  );

  const [isSaving, setIsSaving] = useState(false);

  const handleOnSave = useAtomCallback(
    useCallback(
      async (
        get,
        set,
        args: {
          forcedValues?: {
            nodes?: NoxusReactflowNode[];
            edges?: ReactFlowEdge[];
          };
          isManualSave?: boolean;
        }
      ) => {
        const { forcedValues, isManualSave } = args;
        const nds = forcedValues?.nodes || get(nodesAtom);
        const eds = forcedValues?.edges || get(edgesAtom);

        if (!workflow || cantEdit || (disableAutosave && !isManualSave)) {
          const status = get(saveStateAtom).status;
          return status !== "errored" && status !== "unsaved_changes";
        }

        if (isManualSave) {
          isValidMutation.mutate(
            createFlowDefinition(nds, eds, form?.getValues()).mainFlowDefinition
          );
        }

        clearTimeout(debouncedSaveTimeout.current);
        clearTimeout(debouncedFormChangeTimeout.current);
        debouncedSaveTimeout.current = null;
        debouncedFormChangeTimeout.current = null;

        setIsSaving(true);

        const { mainFlowDefinition } = createFlowDefinition(
          nds,
          eds,
          form?.getValues(),
          runSettings
        );

        const req: UpdateWorkflowRequest = {
          definition: mainFlowDefinition,
          workflow_hash: saveWorkflowHash,
        };

        if (isManualSave) {
          await updateConnectorsFromBackendMutation.mutateAsync({
            id: workflow.id,
            req,
          });
          setIsSaving(false);
          return;
        }

        try {
          const response = await WorkflowService.updateWorkflow({
            path: {
              group_id: spotGroup.id,
              workflow_id: workflow.id,
            },
            body: req,
          });
          queryClient.invalidateQueries({
            queryKey: [DATA_KEYS.WORKFLOWS_ID],
          });
          setSaveWorkflowHash(response.data.workflow_hash || undefined);
          setSaveState({
            timestamp: new Date().toISOString(),
            status: "saved",
          });
          return true;
        } catch (e: any) {
          setSaveState({
            timestamp: new Date().toISOString(),
            status: "errored",
          });
          if ((e as any)?.response?.status === 409) {
            overwriteSaveModalControls.onOpen();
            return false;
          }

          SpotToast.error(
            "There was an error automatically saving. If this problem persists, please contact support."
          );
          Sentry.captureException(e);
          console.error(e);
          return false;
        } finally {
          setIsSaving(false);
        }
      },
      [
        runSettings,
        workflow,
        cantEdit,
        disableAutosave,
        form,
        saveWorkflowHash,
        isValidMutation,
        updateConnectorsFromBackendMutation,
        spotGroup.id,
        queryClient,
        setSaveWorkflowHash,
        setSaveState,
        overwriteSaveModalControls,
      ]
    )
  );

  const updateDynamicInputs = async (nodeId: string, extraInfo?: any) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    try {
      if (node.data.type === "GSpreadsheetWriterNode") {
        let message;
        let inputColumns = extraInfo?.columns || [];

        if (extraInfo?.columns) {
          if (
            extraInfo.columns.find(
              (item: string, index: number) =>
                extraInfo.columns.indexOf(item) !== index
            ) != undefined
          ) {
            // This condition checks for repeated strings on an array
            message = "Spreadsheet columns cannot have duplicate names";
            inputColumns = [];
          } else if (extraInfo.columns.some((item: string) => item === "")) {
            message = "All spreadsheet columns must have a name";
            inputColumns = [];
          } else if (extraInfo.columns.length === 0) {
            message =
              "All spreadsheet columns must have a header on the first row";
            inputColumns = [];
          }
        }

        const copyNode = structuredClone(node);

        copyNode.data.config.inputs = (
          copyNode.data.config.inputs as IODefinition[]
        ).map((i) => {
          //The name "data" comes from the GSheetsWriter node itself - it's the name of the variable_connector
          if (i.name === "data") {
            return {
              ...i,
              keys: inputColumns,
            };
          }
          return i;
        });

        updateNode(nodeId, copyNode, {
          updatesHandles: true,
          clearDanglingEdges: true,
        });

        if (message) {
          throw message;
        }
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
    }
  };

  const updateDynamicOutputs = async (nodeId: string, extraInfo?: any) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    try {
      if (node.data.type === "GSpreadsheetReaderNode") {
        let message;
        let outputColumns = extraInfo?.columns || [];

        if (extraInfo?.columns) {
          if (
            extraInfo.columns.find(
              (item: string, index: number) =>
                extraInfo.columns.indexOf(item) !== index
            ) != undefined
          ) {
            // This conditio checked for repeated strings on an array
            message = "Spreadsheet columns cannot have duplicate names";
            outputColumns = [];
          } else if (extraInfo.columns.some((item: string) => item === "")) {
            message = "All spreadsheet columns must have a name";
            outputColumns = [];
          } else if (extraInfo.columns.length === 0) {
            message =
              "All spreadsheet columns must have a header on the first row";
            outputColumns = [];
          }
        }

        const copyNode = structuredClone(node);

        copyNode.data.config.outputs = (
          copyNode.data.config.outputs as IODefinition[]
        ).map((i) => {
          //The name "data" comes from the GSheetsWriter node itself - it's the name of the variable_connector
          if (i.name === "data") {
            return {
              ...i,
              keys: outputColumns,
            };
          }
          return i;
        });

        updateNode(nodeId, copyNode, {
          updatesHandles: true,
          clearDanglingEdges: true,
        });

        if (message) {
          throw message;
        }
      }

      if (node.data.config.config_endpoint) {
      }
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
    }
  };

  const addNewDynamicInput = (nodeId: string, connector_name: string) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const noSpace = node.data.type === "CodeExecutionNode";

    const multi_connector: IODefinition | undefined =
      node.data.config.inputs.find(
        (i: IODefinition) => i.name === connector_name
      );
    const copyNode = structuredClone(node);
    const maxInputValue = multi_connector?.keys
      ? findMaxStringValue(
          multi_connector?.keys,
          noSpace ? /^input(\d+)$/ : /^Input (\d+)$/
        )
      : 0;

    const new_keys: IODefinition[] = copyNode.data.config.inputs.map(
      (i: IODefinition) => {
        if (i.name === connector_name) {
          return {
            ...i,
            keys: [
              ...(i.keys || []),
              noSpace
                ? `input${maxInputValue + 1}`
                : `Input ${maxInputValue + 1}`,
            ],
          };
        }
        return i;
      }
    );
    copyNode.data.config.inputs = new_keys;

    updateNode(nodeId, copyNode, {
      updatesHandles: true,
    });
  };

  const addNewDynamicOutput = useAtomCallback(
    useCallback(
      (get, _set, nodeId: string, connector_name: string) => {
        const currentNodes = get(nodesAtom);
        const node = currentNodes.find((n) => n.id === nodeId);
        if (!node) return;

        const noSpace = node.data.type === "CodeExecutionNode";

        const multi_connector: IODefinition | undefined =
          node.data.config.outputs.find(
            (i: IODefinition) => i.name === connector_name
          );

        const copyNode = structuredClone(node);
        const maxOutputValue = multi_connector?.keys
          ? findMaxStringValue(
              multi_connector?.keys,
              noSpace ? /^output(\d+)$/ : /^Output (\d+)$/
            )
          : 0;

        const new_keys: IODefinition[] = copyNode.data.config.outputs.map(
          (i: IODefinition) => {
            if (i.name === connector_name) {
              return {
                ...i,
                keys: [
                  ...(i.keys || []),
                  noSpace
                    ? `output${maxOutputValue + 1}`
                    : `Output ${maxOutputValue + 1}`,
                ],
              };
            }
            return i;
          }
        );
        copyNode.data.config.outputs = new_keys;

        updateNode(nodeId, copyNode, { updatesHandles: true });
      },
      [updateNode]
    )
  );

  const removeDynamicOutput = (
    nodeId: string,
    key: string,
    connector_name: string
  ) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);

    copyNode.data.config.outputs = (
      copyNode.data.config.outputs as IODefinition[]
    ).map((i) => {
      if (i.name === connector_name) {
        return { ...i, keys: i.keys?.filter((k) => k !== key) || [] };
      }
      return i;
    });

    setEdges((edg) =>
      edg.filter((e) => e.source !== nodeId || e.sourceHandle !== key)
    );
    updateNode(nodeId, copyNode, { updatesHandles: true });
  };

  const removeDynamicInput = (
    nodeId: string,
    key: string,
    connector_name: string
  ) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const copyNode = structuredClone(node);
    copyNode.data.config.inputs = (
      copyNode.data.config.inputs as IODefinition[]
    ).map((i) => {
      if (i.name === connector_name) {
        return { ...i, keys: i.keys?.filter((k) => k !== key) || [] };
      }
      return i;
    });

    setEdges((edg) =>
      edg.filter((e) => e.target !== nodeId || e.targetHandle !== key)
    );
    updateNode(nodeId, copyNode, { updatesHandles: true });
  };

  const changeDynamicInputName = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        nodeId: string,
        oldName: string,
        newName: string,
        connector_name: string
      ) => {
        const currentNodes = get(nodesAtom);
        const node = currentNodes.find((n) => n.id === nodeId);
        if (!node || oldName === newName) return oldName;

        const copyNode = structuredClone(node);
        const multi_connector: IODefinition | undefined =
          node.data.config.inputs.find(
            (i: IODefinition) => i.name === connector_name
          );

        if (!multi_connector?.keys) {
          return "";
        }

        if (multi_connector.keys.includes(newName)) {
          return oldName;
        }

        copyNode.data.config.inputs = (
          copyNode.data.config.inputs as IODefinition[]
        ).map((i) => {
          if (i.name !== connector_name) {
            return i;
          }
          return {
            ...i,
            keys: i.keys!.map((k) => (k === oldName ? newName : k)),
          };
        });

        if (node.data.type === "InputNode" || node.data.type === "OutputNode") {
          copyNode.data.name = newName;
        }

        setEdges((edg) =>
          edg.map((e) => {
            if (e.target === nodeId && e.targetHandle === oldName) {
              return {
                ...e,
                targetHandle: newName,
              };
            }
            return e;
          })
        );

        updateNode(nodeId, copyNode, { updatesHandles: true });

        if (node.data.type === "ComposeTextNode") {
          let form_content =
            (form?.getValues(`${nodeId}.template`) as string) || "";
          form_content = form_content.replaceAll(
            `((${oldName}))`,
            `((${newName}))`
          );
          form?.setValue(`${nodeId}.template`, form_content);
        }
        return newName;
      },
      [form, setEdges, updateNode]
    )
  );

  const changeDynamicOutputName = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        nodeId: string,
        oldName: string,
        newName: string,
        connector_name: string
      ) => {
        const currentNodes = get(nodesAtom);
        const node = currentNodes.find((n) => n.id === nodeId);
        if (!node) return;

        const copyNode = structuredClone(node);
        const multi_connector: IODefinition | undefined =
          node.data.config.outputs.find(
            (i: IODefinition) => i.type === "variable_connector"
          );

        if (!multi_connector?.keys) {
          return "";
        }

        if (multi_connector.keys.includes(newName)) {
          return oldName;
        }

        copyNode.data.config.outputs = (
          copyNode.data.config.outputs as IODefinition[]
        ).map((i) => {
          if (i.name !== connector_name) {
            return i;
          }
          return {
            ...i,
            keys: i.keys!.map((k) => (k === oldName ? newName : k)),
          };
        });

        if (node.data.type === "InputNode" || node.data.type === "OutputNode") {
          copyNode.data.name = newName;
        }

        updateNode(nodeId, copyNode, {
          updatesHandles: true,
          clearDanglingEdges: true,
        });
      },
      [updateNode]
    )
  );

  const updateMultiTypeDictArrayConnectors = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        nodeId: string,
        connectors: string[],
        value: ITypeDictValue[]
      ) => {
        const currentNodes = get(nodesAtom);
        const node = currentNodes.find((n) => n.id === nodeId);
        if (!node) return;

        const copyNode = structuredClone(node);
        const inputs = structuredClone(
          copyNode.data.config.inputs
        ) as IODefinition[];

        const multi_connector_input_def: IODefinition | undefined = inputs.find(
          (i: IODefinition) => i.type === "variable_type_size_connector"
        );

        if (
          !multi_connector_input_def ||
          !connectors.includes(multi_connector_input_def.name)
        ) {
          return;
        }

        copyNode.data.config.inputs = (
          copyNode.data.config.inputs as IODefinition[]
        ).map((io) => {
          if (io.type === "variable_type_size_connector") {
            return {
              ...io,
              keys: value.map((v) => v.label),
              type_definitions: value.reduce(
                (acc, v) => ({
                  ...acc,
                  [v.label]: {
                    is_list: io.is_list,
                    data_type: v.type.type as DataType,
                  },
                }),
                {} as Record<string, TypeDefinition>
              ),
            };
          }
          return io;
        });

        updateNode(nodeId, copyNode, {
          updatesHandles: true,
          clearDanglingEdges: true,
        });
      },
      [updateNode]
    )
  );

  const unselectAllNodes = useAtomCallback(
    useCallback(
      (get) => {
        const currentNodes = get(nodesAtom);
        setNodes(currentNodes.map((n) => ({ ...n, selected: false })));
      },
      [setNodes]
    )
  );

  // ---------------------------------------
  // ----------- EVENT HANDLERS ------------
  // ---------------------------------------

  const [tmpNodesForHistory, setTmpNodesForHistory] = useState<
    NoxusReactflowNode[]
  >([]);

  const [originalDragPosition, setOriginalDragPosition] =
    useState<XYPosition | null>(null);

  const onNodeDragStart = useAtomCallback(
    useCallback((get, _set, evt: React.MouseEvent, node: any) => {
      if (node.type === "group") {
        return;
      }
      const currentNodes = get(nodesAtom);
      setTmpNodesForHistory(currentNodes);
      setOriginalDragPosition(node.position);
    }, [])
  );

  const onReconnect = useCallback(
    (oldEdge: ReactFlowEdge, newConnection: Connection) => {
      return setEdges((els) => {
        const new_edges = reconnectEdge(oldEdge, newConnection, els);
        return new_edges;
      });
    },
    [setEdges]
  );

  const calculateDistanceBetweenTwoPositions = useCallback(
    (p1: XYPosition, p2: XYPosition) => {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    },
    []
  );

  const onNodeDragStop = useCallback(
    (evt: React.MouseEvent, node: NoxusReactflowNode) => {
      if (!node) return;

      if (
        originalDragPosition &&
        calculateDistanceBetweenTwoPositions(
          originalDragPosition,
          node.position
        ) > 10
      ) {
        debouncedSave({}, { nds: tmpNodesForHistory }, true);
        // if (disableAutosave) {
        setSaveState({
          timestamp: new Date().toISOString(),
          status: "unsaved_changes",
        });
        // }
      }
      setTmpNodesForHistory([]);

      dragRef.current = null;
    },
    [
      calculateDistanceBetweenTwoPositions,
      debouncedSave,
      // disableAutosave,
      originalDragPosition,
      setSaveState,
      tmpNodesForHistory,
    ]
  );
  const onNodesDelete = useAtomCallback(
    useCallback(
      (get, _set, deletedNodes: NoxusReactflowNode[]) => {
        if (cantEdit) return;

        const currentNodes = get(nodesAtom);

        // When deleting a group, also delete its children
        const deletedIds = new Set(deletedNodes.map((node) => node.id));
        for (const node of deletedNodes) {
          if (node.type === "groupNode") {
            for (const child of currentNodes) {
              if (child.parentId === node.id) {
                deletedIds.add(child.id);
              }
            }
          }
        }

        const deletedTypeNamesForAnalyticsOnly = deletedNodes
          .map((node) => node.data?.config?.type)
          .filter((s) => !!s) as string[];

        const currentEdges = get(edgesAtom);

        const nodesThatAreNotInTheDeletedNodesArray = currentNodes.filter(
          (node) => !deletedIds.has(node.id)
        );

        deleting_node_ids = [...deletedIds];

        const is_drawer_open_for_deleted_node = deletedNodes.some(
          ({ id }) => id === drawerControls.openNodeDef?.id
        );
        if (is_drawer_open_for_deleted_node) {
          drawerControls.closeDrawer();
        }

        analytics_track(ANALYTICS_EVENTS.WORKFLOW_DELETE_MODULE, {
          node_types: deletedTypeNamesForAnalyticsOnly,
        });

        const eds = currentEdges.filter((edge) => {
          return !deletedIds.has(edge.source) && !deletedIds.has(edge.target);
        });

        setSaveState({
          timestamp: new Date().toISOString(),
          status: "unsaved_changes",
        });

        debouncedSave({ nds: nodesThatAreNotInTheDeletedNodesArray, eds });
      },
      [cantEdit, debouncedSave, drawerControls, setSaveState]
    )
  );

  const deleteEdgesFromNode = useAtomCallback(
    useCallback(
      (get, _set, node_id: string) => {
        if (cantEdit) return;

        const currentEdges = get(edgesAtom);
        const new_edges = currentEdges.filter(
          (e) => e.source !== node_id && e.target !== node_id
        );
        setEdges(new_edges);
        debouncedSave({ eds: new_edges });
        setSaveState({
          timestamp: new Date().toISOString(),
          status: "unsaved_changes",
        });
      },
      [cantEdit, setEdges, debouncedSave, setSaveState]
    )
  );

  const onEdgesDelete = useAtomCallback(
    useCallback(
      (get, _set, deletedEdges: ReactFlowEdge[]) => {
        if (cantEdit) return;

        const currentEdges = get(edgesAtom);
        const deletedIds = deletedEdges.map((edge) => edge.id);
        const eds = currentEdges.filter((e) => !deletedIds.includes(e.id));
        setEdges(eds);
        debouncedSave({ eds });
        setSaveState({
          timestamp: new Date().toISOString(),
          status: "unsaved_changes",
        });
      },
      [cantEdit, setEdges, debouncedSave, setSaveState]
    )
  );

  const onDragOver = useCallback((event: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const addNewNode = useSetAtom(addNewNodeAtom);

  const onDrop = useCallback(
    (event: any) => {
      const data = event.dataTransfer.getData("application/reactflow");
      if (!data) return;

      event.preventDefault();

      const {
        nodeType,
        subflow_config,
        agent_config,
        plugin_id,
        node_name,
        plugin_display_name,
      } = JSON.parse(data);
      const position = reactFlowInstance?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // To be improved
      if (subflow_config) {
        addNewNode({
          type: nodeType,
          position,
          config: { subflow_config },
          nodesDefinition,
          reactFlowInstance,
          groupId: spotGroup.id,
          form,
        });
      } else if (agent_config) {
        addNewNode({
          type: nodeType,
          position,
          config: { agent_config },
          nodesDefinition,
          reactFlowInstance,
          groupId: spotGroup.id,
          form,
        });
      } else if (plugin_id && node_name) {
        addNewNode({
          type: nodeType,
          position,
          config: { plugin_id, node_name, plugin_display_name },
          nodesDefinition,
          reactFlowInstance,
          groupId: spotGroup.id,
          form,
        });
      } else {
        addNewNode({
          type: nodeType,
          position,
          nodesDefinition,
          reactFlowInstance,
          groupId: spotGroup.id,
          form,
        });
      }
    },
    [addNewNode, nodesDefinition, reactFlowInstance, spotGroup.id, form]
  );

  const onConnect = useAtomCallback(
    useCallback(
      async (get, _set, params: Connection) => {
        if (cantEdit) return;

        const currentEdges = get(edgesAtom);
        const currentNodes = get(nodesAtom);

        const edge = {
          ...params,
          markerEnd: { type: MarkerType.Arrow },
          type: "customEdge",
        };

        const edgeAlreadyExists = currentEdges.some(
          (e) =>
            e.source === edge.source &&
            e.sourceHandle === edge.sourceHandle &&
            e.target === edge.target &&
            e.targetHandle === edge.targetHandle
        );

        if (!edgeAlreadyExists) {
          const edgs = addEdge(edge, currentEdges);
          setEdges(edgs);
          debouncedSave({ eds: edgs });
          // if (disableAutosave) {
          setSaveState({
            timestamp: new Date().toISOString(),
            status: "unsaved_changes",
          });
          // }

          const node_types_for_analytics = {
            target: "",
            source: "",
          };

          for (const n of currentNodes) {
            if (n.id === params.target) {
              node_types_for_analytics.target = n.data.config.type;
            } else if (n.id === params.source) {
              node_types_for_analytics.source = n.data.config.type;
            }
          }

          analytics_track(
            ANALYTICS_EVENTS.WORKFLOW_ADD_CONNECTION,
            node_types_for_analytics
          );
        }
      },
      [cantEdit, setEdges, debouncedSave, setSaveState]
    )
  );

  function handleNodesChange(changes: NodeChange[]) {
    if (cantEdit) return;

    const nextChanges = changes.reduce((acc, change) => {
      if (change.type === "select") {
        return [...acc, change];
      }

      return [...acc, change];
    }, [] as NodeChange[]);

    setNodes((ns) => applyNodeChanges(nextChanges, ns));
  }

  function handleEdgesChange(changes: EdgeChange[]) {
    if (cantEdit) return;

    const nextChanges = changes.reduce((acc, change) => {
      if (change.type === "select") {
        return [...acc, change];
      }

      // all other change types are just put into the next changes arr
      return [...acc, change];
    }, [] as EdgeChange[]);

    // apply the changes we kept
    setEdges((es) => {
      const newEdges = applyEdgeChanges(nextChanges, es);
      return newEdges;
    });
  }

  function updateWorkflowStructure(wfState: IWorkflowState) {
    setNodes([...wfState.nodes]);
    setEdges([...wfState.edges]);
    form?.reset(wfState.form);
  }

  const createUniqueLabelGenerator = useCallback(
    (existingNodes: NoxusReactflowNode[]) => {
      const labelsByType = new Map<string, Set<string>>();

      existingNodes
        .filter((n) => (n.data.config as any)?.no_repeated_names)
        .forEach((n) => {
          const nodeType = (n.data.config as any)?.type;
          const label = form?.getValues(`${n.id}.label`);
          if (nodeType && label) {
            if (!labelsByType.has(nodeType)) {
              labelsByType.set(nodeType, new Set());
            }
            labelsByType.get(nodeType)?.add(label);
          }
        });

      return (baseLabel: string, nodeType: string): string => {
        if (!labelsByType.has(nodeType)) {
          labelsByType.set(nodeType, new Set());
        }

        const existingLabels = labelsByType.get(nodeType)!;
        let newLabel = `${baseLabel} (1)`;
        let counter = 2;

        while (existingLabels.has(newLabel)) {
          newLabel = `${baseLabel} (${counter})`;
          counter++;
        }

        existingLabels.add(newLabel);
        return newLabel;
      };
    },
    [form]
  );

  const copyNodesToClipboard = useAtomCallback(
    useCallback(
      (get, _set, nodesToCopy: NoxusReactflowNode[]) => {
        if (nodesToCopy.length === 0) return;

        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);

        // When copying a group, also include its children
        const expandedNodes = [...nodesToCopy];
        const copiedNodeIds = new Set(nodesToCopy.map((n) => n.id));
        for (const node of nodesToCopy) {
          if (node.type === "groupNode") {
            for (const child of currentNodes) {
              if (child.parentId === node.id && !copiedNodeIds.has(child.id)) {
                expandedNodes.push(child);
                copiedNodeIds.add(child.id);
              }
            }
          }
        }

        const edgesToCopy = currentEdges.filter(
          (edge) =>
            copiedNodeIds.has(edge.source) && copiedNodeIds.has(edge.target)
        );

        const formValues: Record<string, any> = {};
        expandedNodes.forEach((node) => {
          const config = form?.getValues(node.id);
          if (config) {
            formValues[node.id] = config;
          }
        });

        localStorage.setItem(
          "workflow-clipboard",
          JSON.stringify({
            nodes: expandedNodes,
            edges: edgesToCopy,
            formValues,
          })
        );
      },
      [form]
    )
  );

  const cutNodesToClipboard = useAtomCallback(
    useCallback(
      (get, _set, nodesToCut: NoxusReactflowNode[]) => {
        if (nodesToCut.length === 0) return;

        copyNodesToClipboard(nodesToCut);

        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);

        // When cutting a group, also remove its children
        const nodeIds = new Set(nodesToCut.map((n) => n.id));
        for (const node of nodesToCut) {
          if (node.type === "groupNode") {
            for (const child of currentNodes) {
              if (child.parentId === node.id) {
                nodeIds.add(child.id);
              }
            }
          }
        }

        const newNodes = currentNodes.filter((n) => !nodeIds.has(n.id));
        const newEdges = currentEdges.filter(
          (edge) => !nodeIds.has(edge.source) && !nodeIds.has(edge.target)
        );

        setNodes(newNodes);
        setEdges(newEdges);
        debouncedSave({ nds: newNodes, eds: newEdges });
      },
      [copyNodesToClipboard, setNodes, setEdges, debouncedSave]
    )
  );

  const pasteNodesFromClipboard = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        position: { x: number; y: number },
        pasteOffsetRef: React.MutableRefObject<{
          x: number;
          y: number;
          lastX: number;
          lastY: number;
        }>
      ) => {
        const clipboardDataStr = localStorage.getItem("workflow-clipboard");
        if (!clipboardDataStr || !reactFlowInstance) return;

        const currentNodes = get(nodesAtom);
        const currentEdges = get(edgesAtom);

        let clipboardData;
        try {
          clipboardData = JSON.parse(clipboardDataStr);
        } catch (error) {
          console.error("Error parsing clipboard data:", error);
          return;
        }

        let nodesToPaste: NoxusReactflowNode[] = clipboardData.nodes || [];
        const edgesToPaste: any[] = clipboardData.edges || [];
        const formValues: Record<string, any> = clipboardData.formValues || {};

        if (nodesToPaste.length === 0) return;

        // addNewNodeAtom enforces this for the picker; paste was bypassing it.
        const existingTypes = new Set(currentNodes.map((n) => n.data.type));
        const skippedUnique: string[] = [];
        nodesToPaste = nodesToPaste.filter((n) => {
          const isUnique = !!(n.data.config as { unique?: boolean })?.unique;
          if (isUnique && existingTypes.has(n.data.type)) {
            skippedUnique.push(
              (n.data.config as { title?: string })?.title ||
                String(n.data.type)
            );
            return false;
          }
          return true;
        });
        if (skippedUnique.length > 0) {
          SpotToast.warn(
            `Skipped already-present unique node${skippedUnique.length === 1 ? "" : "s"}: ${skippedUnique.join(", ")}`
          );
        }
        if (nodesToPaste.length === 0) return;

        drawerControls?.closeDrawer();

        const clickPosition = reactFlowInstance.screenToFlowPosition(position);

        const threshold = 10;
        const isSameLocation =
          Math.abs(clickPosition.x - pasteOffsetRef.current.lastX) <
            threshold &&
          Math.abs(clickPosition.y - pasteOffsetRef.current.lastY) < threshold;

        if (!isSameLocation) {
          pasteOffsetRef.current = {
            x: 0,
            y: 0,
            lastX: clickPosition.x,
            lastY: clickPosition.y,
          };
        }

        const pastePosition = {
          x: clickPosition.x + pasteOffsetRef.current.x,
          y: clickPosition.y + pasteOffsetRef.current.y,
        };

        pasteOffsetRef.current.x += 30;
        pasteOffsetRef.current.y += 30;

        // Resolve absolute positions for child nodes (their positions are relative to parent)
        const parentNodeMap = new Map(
          nodesToPaste
            .filter((n) => n.type === "groupNode")
            .map((n) => [n.id, n])
        );
        const getAbsolutePosition = (node: NoxusReactflowNode) => {
          if (node.parentId) {
            const parent = parentNodeMap.get(node.parentId);
            if (parent) {
              return {
                x: parent.position.x + node.position.x,
                y: parent.position.y + node.position.y,
              };
            }
          }
          return node.position;
        };

        // Only use top-level nodes for centroid calculation
        const topLevelNodes = nodesToPaste.filter((n) => !n.parentId);
        const allAbsolutePositions =
          topLevelNodes.length > 0
            ? topLevelNodes
            : nodesToPaste.map((n) => ({
                ...n,
                position: getAbsolutePosition(n),
              }));
        const centerX =
          allAbsolutePositions.reduce((sum, node) => sum + node.position.x, 0) /
          allAbsolutePositions.length;
        const centerY =
          allAbsolutePositions.reduce((sum, node) => sum + node.position.y, 0) /
          allAbsolutePositions.length;

        const generateUniqueLabel = createUniqueLabelGenerator(currentNodes);
        const oldToNewIdMap = new Map<string, string>();

        // First pass: generate all new IDs so parentId remapping works
        nodesToPaste.forEach((node) => {
          oldToNewIdMap.set(node.id, uuidv4());
        });

        const copiedNodes = nodesToPaste.map((node) => {
          const new_node_id = oldToNewIdMap.get(node.id)!;

          const config = formValues[node.id] || {};
          Object.entries(config).forEach(([key, value]) => {
            form?.setValue(`${new_node_id}.${key}`, value as string);
          });

          if (node.data.config.no_repeated_names && config?.label) {
            const uniqueLabel = generateUniqueLabel(
              config.label,
              node.data.config.type
            );
            form?.setValue(`${new_node_id}.label`, uniqueLabel);
          }

          // Get the original node definition to preserve correct is_list values
          const originalNodeDef = nodesDefinition?.find(
            (nd) => nd.type === node.data.type
          );

          const newParentId = node.parentId
            ? oldToNewIdMap.get(node.parentId)
            : undefined;

          // If child's parent is being pasted too, keep relative position
          // If child is pasted without parent, convert to absolute position
          let newPosition;
          if (newParentId) {
            // Parent is also being pasted — keep relative position
            newPosition = { x: node.position.x, y: node.position.y };
          } else if (node.parentId) {
            // Child without parent — convert to absolute and offset from paste center
            const absPos = getAbsolutePosition(node);
            newPosition = {
              x: pastePosition.x + (absPos.x - centerX),
              y: pastePosition.y + (absPos.y - centerY),
            };
          } else {
            // Top-level node — offset from paste center
            newPosition = {
              x: pastePosition.x + (node.position.x - centerX),
              y: pastePosition.y + (node.position.y - centerY),
            };
          }

          const newNode = {
            ...node,
            id: new_node_id,
            selected: true,
            ...(newParentId
              ? { parentId: newParentId, extent: "parent" as const }
              : { parentId: undefined, extent: undefined }),
            position: newPosition,
            data: {
              ...node.data,
              config: {
                ...node.data.config,
                inputs: node.data.config.inputs?.map((input: IODefinition) => {
                  // Find the original input definition to get the correct is_list value
                  const originalInput = (
                    originalNodeDef?.inputs as IODefinition[]
                  )?.find(
                    (origInput: IODefinition) => origInput.name === input.name
                  );

                  const resetInput = {
                    ...input,
                    definition: {
                      ...input.definition,
                      is_list: originalInput?.definition?.is_list ?? false,
                    },
                  };

                  if (input.type_definitions) {
                    const resetTypeDefs: Record<string, any> = {};
                    Object.keys(input.type_definitions).forEach((key) => {
                      // Find the original type definition to get the correct is_list value
                      const originalTypeDef =
                        originalInput?.type_definitions?.[key];
                      resetTypeDefs[key] = {
                        ...input.type_definitions![key],
                        is_list: originalTypeDef?.is_list ?? false,
                      };
                    });
                    resetInput.type_definitions = resetTypeDefs;
                  }

                  return resetInput;
                }),
                outputs: node.data.config.outputs?.map(
                  (output: IODefinition) => {
                    // Find the original output definition to get the correct is_list value
                    const originalOutput = (
                      originalNodeDef?.outputs as IODefinition[]
                    )?.find(
                      (origOutput: IODefinition) =>
                        origOutput.name === output.name
                    );

                    const resetOutput = {
                      ...output,
                      definition: {
                        ...output.definition,
                        is_list: originalOutput?.definition?.is_list ?? false,
                      },
                    };

                    if (output.type_definitions) {
                      const resetTypeDefs: Record<string, any> = {};
                      Object.keys(output.type_definitions).forEach((key) => {
                        // Find the original type definition to get the correct is_list value
                        const originalTypeDef =
                          originalOutput?.type_definitions?.[key];
                        resetTypeDefs[key] = {
                          ...output.type_definitions![key],
                          is_list: originalTypeDef?.is_list ?? false,
                        };
                      });
                      resetOutput.type_definitions = resetTypeDefs;
                    }

                    return resetOutput;
                  }
                ),
              },
            },
          };
          return newNode;
        });

        const copiedEdges = edgesToPaste
          .filter(
            (edge) =>
              oldToNewIdMap.has(edge.source) && oldToNewIdMap.has(edge.target)
          )
          .map((edge) => ({
            ...edge,
            id: `${oldToNewIdMap.get(edge.source)}-${edge.sourceHandle}-${oldToNewIdMap.get(edge.target)}-${edge.targetHandle}`,
            source: oldToNewIdMap.get(edge.source) || edge.source,
            target: oldToNewIdMap.get(edge.target) || edge.target,
          }));

        // Sort so group (parent) nodes come before their children
        const sortedCopiedNodes = [...copiedNodes].sort((a, b) => {
          if (a.type === "groupNode" && b.parentId === a.id) return -1;
          if (b.type === "groupNode" && a.parentId === b.id) return 1;
          return 0;
        });

        setNodes([
          ...currentNodes.map((n) => ({ ...n, selected: false })),
          ...sortedCopiedNodes,
        ]);
        setEdges([
          ...currentEdges.map((e) => ({ ...e, selected: false })),
          ...copiedEdges,
        ]);
      },
      [
        reactFlowInstance,
        drawerControls,
        createUniqueLabelGenerator,
        form,
        setNodes,
        setEdges,
        nodesDefinition,
      ]
    )
  );
  const isSavingWorkflow =
    isSaving ||
    updateConnectorsFromBackendMutation.status === "pending" ||
    fastSaveMutation.status === "pending";

  const hasPendingSave =
    !!debouncedSaveTimeout.current || !!debouncedFormChangeTimeout.current;

  return {
    nodes,
    edges,
    connectionLineStyle,
    isSavingWorkflow,
    hasPendingSave,
    saveState,
    history,

    nodes_fetching_config,
    methods: {
      getNodeById,
      setNodes,
      setEdges,
      handleOnSave,
      updateNodeName,
      getKBNode,
      addNewNode,
      isNodeDefined,
      isValidConnection,
      deleteNodesById,
      updateDynamicInputs,
      updateDynamicOutputs,
      addNewDynamicInput,
      removeDynamicInput,
      addNewDynamicOutput,
      removeDynamicOutput,
      changeDynamicOutputName,
      changeDynamicInputName,
      deleteEdgesFromNode,
      saveAndUpdateTypes: debouncedSave,
      updateMultiTypeDictArrayConnectors,
      unselectAllNodes,
      getDynamicConfigsFromBackend,
      asyncFastSave,
      asyncFastSaveForTabChange,
      updateWorkflowStructure,
      getCurrentFlowDefinition,
      isValidMutation,
      copyNodesToClipboard,
      cutNodesToClipboard,
      pasteNodesFromClipboard,
    },
    handlers: {
      onNodesChange: handleNodesChange,
      onEdgesChange: handleEdgesChange,
      onConnect,
      onNodeDragStart,
      onNodeDragStop,
      onDrop,
      onNodesDelete,
      onReconnect,
      onEdgesDelete,
      onDragOver,
    },
    history_controls,
  };
};
